import type { SearchResult, SearchResultType } from "./GlobalSearchDialog";

/** Must stay in sync with filter chips in GlobalSearchDialog. */
export const MAIN_ASSET_TYPES = [
  "client",
  "task",
  "product",
  "portfolio",
  "benchmark",
  "index",
] as const;

/**
 * Splits grouped search results for the active filter tab (All / asset type / Other).
 */
export function partitionSearchResultsForFilter(
  groupedResults: Record<string, SearchResult[]>,
  quickNavTypes: string[],
  selectedFilter: SearchResultType | "all",
): {
  quickNavResults: SearchResult[];
  otherSections: [string, SearchResult[]][];
} {
  const isShowingAll = selectedFilter === "all";
  const isShowingOther = selectedFilter === "app";
  const isShowingMainType = (MAIN_ASSET_TYPES as readonly string[]).includes(
    selectedFilter,
  );

  const quickNavResults =
    isShowingAll || isShowingOther
      ? quickNavTypes.flatMap((type) => groupedResults[type] || [])
      : [];

  const otherSections = Object.entries(groupedResults)
    .filter(([type]) => !quickNavTypes.includes(type))
    .filter(([type]) => {
      if (isShowingAll) return true;
      if (isShowingOther) return !(MAIN_ASSET_TYPES as readonly string[]).includes(type);
      if (isShowingMainType) return type === selectedFilter;
      return false;
    });

  return { quickNavResults, otherSections };
}

/**
 * Removes duplicate destinations (e.g. screener linked from both nav and registry).
 */
export function dedupeSearchResultsByUrl(results: SearchResult[]): SearchResult[] {
  const seen = new Set<string>();
  return results.filter((result) => {
    if (seen.has(result.url)) {
      return false;
    }
    seen.add(result.url);
    return true;
  });
}

const PERFORMANCE_DATA_TYPES = new Set(["product", "index", "benchmark"]);

export function formatPerformanceDataFromDate(
  firstRealDate: string | null | undefined,
): string {
  const date =
    firstRealDate != null && String(firstRealDate).trim()
      ? String(firstRealDate).trim()
      : "n.a.";
  return `Performance data from: ${date}`;
}

/** Grey subtitle for screener-backed product / index / benchmark rows. */
export function buildScreenerAssetSearchDescription(
  resourceType: string,
  options: {
    isin?: string | null;
    currency?: string | null;
    firstRealDate?: string | null;
  },
): string {
  const normalizedType = resourceType.toLowerCase();
  const capitalizedType =
    normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1);

  if (normalizedType === "product") {
    const parts = [capitalizedType];
    if (options.isin) parts.push(String(options.isin));
    if (options.currency) parts.push(String(options.currency));
    parts.push(formatPerformanceDataFromDate(options.firstRealDate));
    return parts.join(" • ");
  }

  if (PERFORMANCE_DATA_TYPES.has(normalizedType)) {
    const parts = [capitalizedType];
    if (options.currency) parts.push(String(options.currency));
    parts.push(formatPerformanceDataFromDate(options.firstRealDate));
    return parts.join(" • ");
  }

  let description = capitalizedType;
  if (options.currency) {
    description += ` • ${String(options.currency)}`;
  }
  return description;
}
